import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import {
  useGetMyStudentProfileQuery,
  useGetStudentReportsQuery,
} from "../../../app/parentApiSlice";
import UserAvatar from "../../common/UserAvatar";

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}:
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function ReportPreviewModal({ url, open, onClose }) {
  const [iframeLoading, setIframeLoading] = useState(true);
  const googleDocsUrl = url
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "90vh" } }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        Report Preview
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden", position: "relative", bgcolor: "#fff" }}>
        {iframeLoading && googleDocsUrl && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.85)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {googleDocsUrl && (
          <iframe
            key={url}
            src={googleDocsUrl}
            title="Report Preview"
            width="100%"
            height="100%"
            style={{ border: "none", backgroundColor: "#fff" }}
            onLoad={() => setIframeLoading(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function StudentProfile({ student }) {
  const fullName =
    `${student.first_name || ""} ${student.last_name || ""}`.trim();

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
        <UserAvatar
          src={student.photo}
          name={fullName}
          sx={{ width: 80, height: 80, fontSize: "1.8rem" }}
        />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {fullName || "—"}
          </Typography>
          {student.reg_number && (
            <Typography variant="body2" color="text.secondary">
              Reg No: {student.reg_number}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <InfoRow label="Gender" value={student.gender} />
          <InfoRow label="Email" value={student.email} />
          <InfoRow label="Phone" value={student.phone} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InfoRow label="Address" value={student.address} />
          <InfoRow
            label="Date of Birth"
            value={
              student.dob
                ? new Date(student.dob).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })
                : null
            }
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

function ReportsSection({ studentId }) {
  const { data: reports, isLoading, error } = useGetStudentReportsQuery(studentId);
  const [previewUrl, setPreviewUrl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handlePreview = (url) => {
    if (isMobile) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      setPreviewUrl(url);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !reports?.length) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        No reports available yet.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Term</StyledTableCell>
              <StyledTableCell>Session</StyledTableCell>
              <StyledTableCell>Class</StyledTableCell>
              <StyledTableCell>Generated</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <StyledTableRow key={report.id}>
                <StyledTableCell>{report.term?.name || "—"}</StyledTableCell>
                <StyledTableCell>{report.session?.name || "—"}</StyledTableCell>
                <StyledTableCell>
                  {report.classroom?.name || "—"}
                </StyledTableCell>
                <StyledTableCell>
                  {report.created_at
                    ? new Date(report.created_at).toLocaleDateString("en-GB")
                    : "—"}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {report.file_url ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "center",
                      }}
                    >
                      <Tooltip title="Preview report">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(report.file_url)}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download report">
                        <IconButton
                          size="small"
                          component="a"
                          href={report.file_url}
                          download
                        >
                          <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    "—"
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReportPreviewModal
        url={previewUrl}
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </>
  );
}

function LmsAccessSection() {
  const navigate = useNavigate();

  const lmsCards = [
    {
      title: "Class Quiz",
      description: "Take quizzes assigned to your class",
      icon: <QuizOutlinedIcon sx={{ fontSize: 32, color: "#2980b9" }} />,
      path: "/lms/student/class-quiz",
      accent: "#2980b9",
    },
    {
      title: "Game Arena",
      description: "Battle classmates, join tournaments or practice solo",
      icon: <SportsEsportsIcon sx={{ fontSize: 32, color: "#e02424" }} />,
      path: "/lms/student/quiz/play",
      accent: "#e02424",
    },
  ];

  return (
    <Paper
      sx={{
        p: 3,
        mb: 3,
        background: "linear-gradient(135deg, #1A1A1A 0%, #2c3e50 100%)",
        color: "#fff",
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <MenuBookOutlinedIcon sx={{ color: "#FFA534" }} />
        <Typography variant="h6" fontWeight="bold">
          Learning Management System
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "#bbb", mb: 3 }}>
        Access your online learning materials, take quizzes and track your progress.
      </Typography>
      <Grid container spacing={2}>
        {lmsCards.map((card) => (
          <Grid item xs={12} sm={6} key={card.title}>
            <Box
              onClick={() => navigate(card.path)}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.07)",
                border: `1px solid rgba(255,255,255,0.1)`,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "background-color 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.13)",
                  borderColor: card.accent,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight={700}>
                    {card.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#aaa" }}>
                    {card.description}
                  </Typography>
                </Box>
              </Box>
              <ArrowForwardIcon sx={{ color: "#FFA534", flexShrink: 0 }} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

function StudentPortal() {
  const { data: student, isLoading, error } = useGetMyStudentProfileQuery();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !student) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Could not load your student profile. Please contact your school administrator.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <StudentProfile student={student} />

      <LmsAccessSection />

      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
        My Reports
      </Typography>

      <ReportsSection studentId={student.id} />
    </Box>
  );
}

export default StudentPortal;
